import type { VoyageReportKind } from '@felix/domain';
import { Alert, Button, Select, TextInput } from '@mantine/core';
import {
  IconArrowDownRight,
  IconArrowRight,
  IconArrowUpRight,
  IconChartBar,
  IconChartLine,
  IconFileText,
  IconGasStation,
  IconScale,
} from '@tabler/icons-react';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { followLink, useShell } from '../../shell-context.js';
import { renderPdf } from '../personnel/pdf.js';
import { errorCode } from '../personnel/source.js';
import { LoadError, TableSkeleton } from '../purchasing/table.js';
import { downloadFile } from '../reports/xlsx.js';
import { useReportLabels } from '../voyage/report-labels.js';
import classes from './Ledger.module.css';
import { atText, mt } from './ledger-ui.js';
import { type ReconciliationReportInput, reconciliationReportPdf } from './reconciliation-form.js';
import type { FuelSource } from './source.js';
import type { FuelConsumptionDaily, FuelOverviewData } from './types.js';

/** Günlük tüketim pencereleri (gün; ADR 0117 Ek Y3). */
const WINDOWS = [7, 14, 30] as const;

export interface OverviewPanelProps {
  source: FuelSource;
  vesselId: string | null;
  onVessel?: (data: FuelOverviewData) => void;
}

const pct = (value: string | null) =>
  value === null ? null : Math.max(0, Math.min(100, Math.round(Number(value))));

const CHART_W = 640;
const CHART_H = 220;
const PAD = { left: 40, right: 16, top: 12, bottom: 30 };

/** Eksen üst sınırı: en büyük değerin üstündeki yuvarlak sayı (5, 10, 20, 30 …). */
export function axisMax(max: number): number {
  if (max <= 0) return 10;
  const step = 10 ** Math.floor(Math.log10(max));
  for (const factor of [1, 2, 3, 5, 10]) {
    if (factor * step >= max) return factor * step;
  }
  return 10 * step;
}

/**
 * Yakıt Tüketimi (Günlük) grafiği (ortağın 01-genel-bakis.jpeg): gün başına MT, yakıt başına çizgi
 * ve nokta; yatay ızgara ve gün etiketleri. Noktanın ipucunda gün ve değer; aynı veri ekran
 * okuyucuya figür açıklamasıyla verilir.
 */
function ConsumptionChart({
  daily,
  label,
  fuelCode,
  fuelClass,
}: {
  daily: FuelConsumptionDaily;
  label: string;
  fuelCode: (id: string | null) => string;
  fuelClass: (id: string | null) => string | undefined;
}) {
  const { t } = useTranslation();
  const values = daily.series.flatMap((s) => s.values.map(Number));
  const top = axisMax(Math.max(0, ...values));
  const width = CHART_W - PAD.left - PAD.right;
  const height = CHART_H - PAD.top - PAD.bottom;
  const x = (index: number) =>
    PAD.left + (daily.days.length > 1 ? (index / (daily.days.length - 1)) * width : width / 2);
  const y = (value: number) => PAD.top + height - (value / top) * height;
  const ticks = [0, top / 3, (2 * top) / 3, top];
  const dayLabel = (day: string) => `${day.slice(8, 10)}.${day.slice(5, 7)}`;
  const every = Math.ceil(daily.days.length / 8);
  return (
    <figure className={classes.chart}>
      <div className={classes.chartHead}>
        <span className={classes.hint}>{t('fuel.overview.chartAxis')}</span>
        <span className={classes.legend}>
          {daily.series.map((s) => (
            <span className={classes.legendItem} key={s.fuelTypeId}>
              <span className={`${classes.dot} ${fuelClass(s.fuelTypeId) ?? ''}`} aria-hidden />
              {t('fuel.overview.chartSeries', { fuel: fuelCode(s.fuelTypeId) })}
            </span>
          ))}
        </span>
      </div>
      <svg
        viewBox={`0 0 ${CHART_W} ${CHART_H}`}
        className={classes.chartSvg}
        role="img"
        aria-label={label}
      >
        {ticks.map((tick) => (
          <g key={tick}>
            <line
              x1={PAD.left}
              x2={CHART_W - PAD.right}
              y1={y(tick)}
              y2={y(tick)}
              className={classes.chartGrid}
            />
            <text x={PAD.left - 8} y={y(tick) + 4} className={classes.chartTick} textAnchor="end">
              {Math.round(tick * 10) / 10}
            </text>
          </g>
        ))}
        {daily.days.map((day, index) =>
          index % every === 0 || index === daily.days.length - 1 ? (
            <text
              key={day}
              x={x(index)}
              y={CHART_H - 8}
              className={classes.chartTick}
              textAnchor="middle"
            >
              {dayLabel(day)}
            </text>
          ) : null,
        )}
        {daily.series.map((s) => {
          const path = s.values
            .map((v, i) => `${i === 0 ? 'M' : 'L'}${x(i)},${y(Number(v))}`)
            .join(' ');
          const area = `${path} L${x(s.values.length - 1)},${y(0)} L${x(0)},${y(0)} Z`;
          return (
            <g
              key={s.fuelTypeId}
              className={`${classes.chartSeries} ${fuelClass(s.fuelTypeId) ?? ''}`}
            >
              <path d={area} className={classes.chartArea} />
              <path d={path} className={classes.chartLine} />
              {s.values.map((v, i) => (
                <circle
                  key={daily.days[i]}
                  cx={x(i)}
                  cy={y(Number(v))}
                  r={4}
                  className={classes.chartPoint}
                >
                  <title>
                    {t('fuel.overview.chartPoint', {
                      day: dayLabel(daily.days[i] ?? ''),
                      value: mt(v),
                    })}
                  </title>
                </circle>
              ))}
            </g>
          );
        })}
      </svg>
    </figure>
  );
}

/**
 * Genel Bakış (ortağın 01-genel-bakis.jpeg tasarımı; ortak belgesi §5; ADR 0117 Ek Y2): Gemi ve
 * Sefer Bilgileri, Güncel ROB kartları (yakıt başına MT, kapasite ve hacme dayalı doluluk), Tank
 * Doluluk Durumu, Son Yakıt Hareketleri. Hepsi onaylı defter hareketlerinden; yeni kayıt
 * üretmez. Günlük tüketim (Y3) ve ROB mutabakatı (Y4) kartları boş durumla.
 */
export function OverviewPanel({ source, vesselId, onVessel }: OverviewPanelProps) {
  const { t } = useTranslation();
  const shell = useShell();
  const labels = useReportLabels();
  const [data, setData] = useState<FuelOverviewData | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [days, setDays] = useState<number>(7);
  const [reporting, setReporting] = useState(false);
  const [reportError, setReportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const next = await source.overview(vesselId, days);
      setData(next);
      setFailed(null);
      onVessel?.(next);
    } catch (error) {
      setFailed(errorCode(error));
    }
  }, [source, vesselId, onVessel, days]);

  useEffect(() => {
    void load();
  }, [load]);

  if (failed) {
    return <LoadError code={failed} onRetry={() => void load()} title={t('fuel.tabs.overview')} />;
  }
  if (!data) return <TableSkeleton columns={4} rows={8} label={t('fuel.tabs.overview')} />;

  const fuelCode = (id: string | null) =>
    data.fuelTypes.find((f) => f.id === id)?.code ?? t('fuel.overview.other');
  const fuelIndex = new Map(data.fuels.map((f, index) => [f.fuelTypeId, Math.min(index, 2)]));
  const fuelClass = (id: string | null) =>
    classes[`fuel${id !== null && fuelIndex.has(id) ? fuelIndex.get(id) : 2}`];
  const tankLabel = (id: string | null) => {
    const tank = data.tanks.find((x) => x.id === id);
    return tank ? tank.code : '';
  };
  const voyage = data.voyage;
  const daily = data.consumption ?? null;
  const card = daily?.card ?? null;
  const change = card?.changePct ? Number(card.changePct) : null;
  const drift = data.snapshotDrift ?? [];
  const measured = (fuelTypeId: string) =>
    (data.measured ?? []).find((m) => m.fuelTypeId === fuelTypeId) ?? null;
  const recon = data.reconciliation ?? null;
  const createReport = async () => {
    if (!recon) return;
    setReporting(true);
    setReportError(null);
    try {
      const view =
        recon.soundingId && source.reconciliation
          ? await source.reconciliation(vesselId, recon.soundingId)
          : null;
      const tankName = (id: string) => data.tanks.find((x) => x.id === id)?.name ?? id;
      const bytes = await renderPdf(
        reconciliationReportPdf({
          vesselName: data.vessel.name,
          card: recon,
          sounding: view?.sounding ?? null,
          fuelCode,
          tankName,
          dateTime: (iso) => atText(iso, recon.tzOffsetMin ?? 0),
          mt: (value) => mt(value),
          printedAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
          labels: {
            ...(t('fuel.rob.report', { returnObjects: true }) as Record<string, string>),
            sounding: view?.sounding
              ? t('fuel.rob.report.sounding', {
                  no: view.sounding.soundingNo,
                  at: atText(view.sounding.measuredAt, view.sounding.tzOffsetMin),
                })
              : '',
            tankColumns: [
              t('fuel.rob.columns.tank'),
              t('fuel.rob.columns.method'),
              t('fuel.rob.columns.reading'),
              t('fuel.rob.columns.temp'),
              t('fuel.rob.columns.volume'),
              t('fuel.rob.columns.density'),
              t('fuel.rob.columns.measured'),
            ],
          } as unknown as ReconciliationReportInput['labels'],
        }),
      );
      const name = `rob-reconciliation-${data.vessel.name.replace(/[^A-Za-z0-9]+/g, '-')}-${recon.to.slice(0, 10)}.pdf`;
      if (source.saveFile) await source.saveFile(name, bytes, 'application/pdf');
      else downloadFile(name, bytes, 'application/pdf');
    } catch (failure) {
      setReportError(
        t(`fuel.errors.${errorCode(failure)}`, { defaultValue: t('fuel.errors.GENERIC') }),
      );
    } finally {
      setReporting(false);
    }
  };
  const go = (path: string) => (event: React.MouseEvent<HTMLAnchorElement>) => {
    if (shell) followLink(event, path, shell.navigate);
  };

  return (
    <div className={classes.alerts}>
      {!data.ledgerOpen ? (
        <Alert color="blue" role="note">
          {t('fuel.overview.noLedger')}
        </Alert>
      ) : null}
      {data.pendingRecords > 0 ? (
        <Alert color="yellow" role="status">
          {t('fuel.overview.pending', { count: data.pendingRecords })}
        </Alert>
      ) : null}
      {drift.length > 0 ? (
        <Alert color="yellow" role="status">
          {t('fuel.overview.snapshotDrift', { count: drift.length })}
        </Alert>
      ) : null}
      {data.negativeTanks.length > 0 ? (
        <Alert color="red" role="alert">
          {t('fuel.overview.negative', {
            tanks: data.negativeTanks.map(tankLabel).join(', '),
          })}
        </Alert>
      ) : null}

      <div className={classes.overview}>
        <section
          className={`${classes.card} ${classes.panel}`}
          aria-label={t('fuel.overview.vesselInfo')}
        >
          <h2 className={classes.panelTitle}>{t('fuel.overview.vesselInfo')}</h2>
          <div className={classes.vesselGrid}>
            <div className={classes.vesselRow}>
              <span className={classes.fieldLabel}>{t('fuel.overview.vessel')}</span>
              <TextInput aria-label={t('fuel.overview.vessel')} value={data.vessel.name} readOnly />
            </div>
            <TextInput
              label={t('fuel.overview.currentVoyage')}
              value={voyage?.voyageNo ?? t('fuel.overview.noVoyage')}
              readOnly
            />
            <TextInput
              label={t('fuel.overview.departure')}
              value={voyage?.fromPort ?? '—'}
              readOnly
            />
            <TextInput label={t('fuel.overview.arrival')} value={voyage?.toPort ?? '—'} readOnly />
            <TextInput
              label={t('fuel.overview.etd')}
              value={
                voyage?.plannedEtd ? atText(voyage.plannedEtd, voyage.plannedEtdOffset ?? 0) : '—'
              }
              readOnly
            />
            <TextInput
              label={t('fuel.overview.eta')}
              value={
                voyage?.plannedEta ? atText(voyage.plannedEta, voyage.plannedEtaOffset ?? 0) : '—'
              }
              readOnly
            />
            <div>
              <span className={classes.fieldLabel}>{t('fuel.overview.voyageStatus')}</span>
              <div style={{ marginTop: 4 }}>
                {voyage ? (
                  <span className={classes.statusPill}>
                    {t(`fuel.overview.status.${voyage.status}`)}
                  </span>
                ) : (
                  '—'
                )}
              </div>
            </div>
          </div>
        </section>

        <section
          className={`${classes.card} ${classes.panel}`}
          aria-label={t('fuel.overview.currentRob')}
        >
          <h2 className={classes.panelTitle}>{t('fuel.overview.currentRob')}</h2>
          <div className={classes.robCards}>
            {data.fuels.map((fuel) => {
              const fill = pct(fuel.fillPct);
              return (
                <div className={classes.robCard} key={fuel.fuelTypeId}>
                  <span className={classes.robFuel}>
                    <IconGasStation size={22} aria-hidden />
                    {fuelCode(fuel.fuelTypeId)}
                  </span>
                  <span className={classes.robValue}>{mt(fuel.robMt)} MT</span>
                  <div className={classes.bar} aria-hidden>
                    <div
                      className={`${classes.barFill} ${fuelClass(fuel.fuelTypeId)}`}
                      style={{ width: `${fill ?? 0}%` }}
                    />
                  </div>
                  <div className={classes.robMeta}>
                    <span>
                      {t('fuel.overview.totalCapacity')}
                      <strong>
                        {fuel.capacityMt
                          ? `${mt(fuel.capacityMt)} MT`
                          : t('fuel.overview.capacityUnknown')}
                      </strong>
                    </span>
                    {fill !== null ? (
                      <span className={classes.robPct}>
                        {t('fuel.overview.percent', { value: fill })}
                      </span>
                    ) : null}
                  </div>
                  {measured(fuel.fuelTypeId) ? (
                    <span className={classes.hint}>
                      {t('fuel.overview.lastMeasured', {
                        value: mt(measured(fuel.fuelTypeId)?.measuredMt),
                        at: atText(
                          measured(fuel.fuelTypeId)?.at,
                          measured(fuel.fuelTypeId)?.tzOffsetMin ?? 0,
                        ),
                      })}
                    </span>
                  ) : null}
                </div>
              );
            })}
            <div className={classes.robCard}>
              <span className={classes.robFuel}>
                <IconChartBar size={22} aria-hidden />
                {card
                  ? t('fuel.overview.dailyConsumptionOf', { fuel: fuelCode(card.fuelTypeId) })
                  : t('fuel.overview.dailyConsumption')}
              </span>
              <span className={classes.robValue}>{card ? `${mt(card.massMt)} MT` : '—'}</span>
              {card ? (
                <div className={classes.robMeta}>
                  <span>
                    {t('fuel.overview.average', { days: daily?.days.length ?? 7 })}
                    <strong>{t('fuel.overview.perDay', { value: mt(card.averageMt) })}</strong>
                  </span>
                  {change !== null && change !== 0 ? (
                    <span
                      className={change > 0 ? classes.positive : classes.negative}
                      title={t(change > 0 ? 'fuel.overview.changeUp' : 'fuel.overview.changeDown', {
                        value: Math.abs(change),
                      })}
                    >
                      {change > 0 ? (
                        <IconArrowUpRight size={16} aria-hidden />
                      ) : (
                        <IconArrowDownRight size={16} aria-hidden />
                      )}
                      {t('fuel.overview.percent', { value: Math.abs(change) })}
                    </span>
                  ) : null}
                </div>
              ) : (
                <span className={classes.hint}>{t('fuel.overview.dailyConsumptionEmpty')}</span>
              )}
            </div>
          </div>
        </section>
      </div>

      <div className={classes.overviewRow}>
        <section
          className={`${classes.card} ${classes.panel}`}
          aria-label={t('fuel.overview.tankLevels')}
        >
          <div className={classes.panelHead}>
            <h2 className={classes.panelTitle}>{t('fuel.overview.tankLevels')}</h2>
            <div className={classes.legend}>
              {data.fuels.slice(0, 2).map((fuel) => (
                <span className={classes.legendItem} key={fuel.fuelTypeId}>
                  <span className={`${classes.dot} ${fuelClass(fuel.fuelTypeId)}`} aria-hidden />
                  {fuelCode(fuel.fuelTypeId)}
                </span>
              ))}
              <span className={classes.legendItem}>
                <span className={`${classes.dot} ${classes.fuel2}`} aria-hidden />
                {t('fuel.overview.other')}
              </span>
            </div>
          </div>
          <div
            className={classes.tableWrap}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={classes.table}>
              <thead>
                <tr>
                  <th>{t('fuel.overview.columns.tank')}</th>
                  <th>{t('fuel.overview.columns.product')}</th>
                  <th className={classes.num}>{t('fuel.overview.columns.robMt')}</th>
                  <th className={classes.num}>{t('fuel.overview.columns.capacityMt')}</th>
                  <th>{t('fuel.overview.columns.fill')}</th>
                </tr>
              </thead>
              <tbody>
                {data.tanks.length === 0 ? (
                  <tr>
                    <td colSpan={5}>{t('fuel.overview.noTanks')}</td>
                  </tr>
                ) : (
                  data.tanks.map((tank) => {
                    const fill = pct(tank.fillPct);
                    return (
                      <tr key={tank.id}>
                        <td>{tank.name}</td>
                        <td>{fuelCode(tank.fuelTypeId)}</td>
                        <td
                          className={`${classes.num} ${tank.robMt.startsWith('-') ? classes.negative : ''}`}
                        >
                          {mt(tank.robMt)}
                        </td>
                        <td className={classes.num}>
                          {tank.capacityMt ? mt(tank.capacityMt) : '—'}
                        </td>
                        <td>
                          <div className={classes.fillCell}>
                            <div className={classes.bar} aria-hidden>
                              <div
                                className={`${classes.barFill} ${fuelClass(tank.fuelTypeId)}`}
                                style={{ width: `${fill ?? 0}%` }}
                              />
                            </div>
                            <span>
                              {fill !== null ? t('fuel.overview.percent', { value: fill }) : '—'}
                            </span>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          <p className={classes.hint}>{t('fuel.overview.volumeHint')}</p>
        </section>

        <section
          className={`${classes.card} ${classes.panel}`}
          aria-label={t('fuel.overview.consumptionChart')}
        >
          <div className={classes.panelHead}>
            <h2 className={classes.panelTitle}>{t('fuel.overview.consumptionChart')}</h2>
            <Select
              className={classes.windowSelect}
              aria-label={t('fuel.overview.window')}
              value={String(days)}
              allowDeselect={false}
              data={WINDOWS.map((value) => ({
                value: String(value),
                label: t('fuel.overview.windowOption', { days: value }),
              }))}
              onChange={(value) => {
                if (value) setDays(Number(value));
              }}
            />
          </div>
          {daily && daily.series.length > 0 ? (
            <ConsumptionChart
              daily={daily}
              label={t('fuel.overview.consumptionChart')}
              fuelCode={fuelCode}
              fuelClass={fuelClass}
            />
          ) : (
            <div className={classes.empty}>
              <IconChartLine size={36} className={classes.emptyIcon} aria-hidden />
              {t('fuel.overview.consumptionChartEmpty')}
            </div>
          )}
        </section>
      </div>

      <div className={`${classes.overviewRow} ${classes.overviewWide}`}>
        <section
          className={`${classes.card} ${classes.panel}`}
          aria-label={t('fuel.overview.movements')}
        >
          <div className={classes.panelHead}>
            <h2 className={classes.panelTitle}>{t('fuel.overview.movements')}</h2>
            <a className={classes.link} href="/fuel/transfers" onClick={go('/fuel/transfers')}>
              {t('fuel.overview.viewAll')} <IconArrowRight size={14} aria-hidden />
            </a>
          </div>
          <div
            className={classes.tableWrap}
            // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
            tabIndex={0}
          >
            <table className={`${classes.table} ${classes.compact}`}>
              <thead>
                <tr>
                  <th>{t('fuel.overview.movementColumns.at')}</th>
                  <th>{t('fuel.overview.movementColumns.type')}</th>
                  <th>{t('fuel.overview.movementColumns.product')}</th>
                  <th className={classes.num}>{t('fuel.overview.movementColumns.mass')}</th>
                  <th>{t('fuel.overview.movementColumns.route')}</th>
                  <th>{t('fuel.overview.movementColumns.description')}</th>
                  <th>{t('fuel.overview.movementColumns.user')}</th>
                </tr>
              </thead>
              <tbody>
                {data.movements.length === 0 ? (
                  <tr>
                    <td colSpan={7}>{t('fuel.overview.noMovements')}</td>
                  </tr>
                ) : (
                  data.movements.map((m) => (
                    <tr key={m.postingId}>
                      <td style={{ whiteSpace: 'nowrap' }}>{atText(m.at, m.tzOffsetMin)}</td>
                      <td>
                        <span className={`${classes.typeBadge} ${classes[`type_${m.type}`] ?? ''}`}>
                          {t(`fuel.overview.movementTypes.${m.type}`, { defaultValue: m.type })}
                        </span>
                      </td>
                      <td>{fuelCode(m.fuelTypeId)}</td>
                      <td className={classes.num}>{mt(m.massMt)}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {m.sourceTankId && m.targetTankId
                          ? `${tankLabel(m.sourceTankId)} → ${tankLabel(m.targetTankId)}`
                          : tankLabel(m.targetTankId ?? m.sourceTankId)}
                      </td>
                      <td>
                        {m.description ??
                          (m.reportKind
                            ? labels.typeLabel(m.reportKind as VoyageReportKind)
                            : m.recordNo) ??
                          '—'}
                      </td>
                      <td>{m.userName ?? '—'}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>

        <section
          className={`${classes.card} ${classes.panel}`}
          aria-label={t('fuel.overview.reconciliation')}
        >
          <div className={classes.panelHead}>
            <h2 className={classes.panelTitle}>
              {t('fuel.overview.reconciliation')}
              {recon?.voyageNo ? <small> ({recon.voyageNo})</small> : null}
            </h2>
            <a
              className={classes.link}
              href="/fuel/rob-reconciliation"
              onClick={go('/fuel/rob-reconciliation')}
            >
              {t('fuel.overview.viewAll')} <IconArrowRight size={14} aria-hidden />
            </a>
          </div>
          {recon && recon.fuels.length > 0 ? (
            <>
              <div
                className={classes.tableWrap}
                // biome-ignore lint/a11y/noNoninteractiveTabindex: kaydırılabilir bölge (WCAG 2.1.1)
                tabIndex={0}
              >
                <table className={`${classes.table} ${classes.compact} ${classes.reconTable}`}>
                  <thead>
                    <tr>
                      <th>{t('fuel.rob.report.product')}</th>
                      <th className={classes.num}>{t('fuel.rob.report.opening')}</th>
                      <th className={classes.num}>{t('fuel.rob.report.bunkered')}</th>
                      <th className={classes.num}>{t('fuel.rob.report.consumed')}</th>
                      <th className={classes.num}>{t('fuel.rob.report.calculated')}</th>
                      <th className={classes.num}>{t('fuel.rob.report.measured')}</th>
                      <th className={classes.num}>{t('fuel.rob.report.diff')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recon.fuels.map((row) => (
                      <tr key={row.fuelTypeId}>
                        <th scope="row">{fuelCode(row.fuelTypeId)}</th>
                        <td className={classes.num}>{mt(row.openingMt)}</td>
                        <td className={classes.num}>{mt(row.bunkeredMt)}</td>
                        <td className={classes.num}>{mt(row.consumedMt)}</td>
                        <td className={classes.num}>{mt(row.calculatedMt)}</td>
                        <td className={classes.num}>{mt(row.measuredMt)}</td>
                        <td
                          className={`${classes.num} ${
                            row.diffMt?.startsWith('-') ? classes.negative : ''
                          }`}
                        >
                          {mt(row.diffMt)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className={classes.hint}>
                {recon.soundingId
                  ? t('fuel.overview.reconciliationAt', {
                      at: atText(recon.to, recon.tzOffsetMin ?? 0),
                    })
                  : t('fuel.overview.reconciliationNoSounding')}
              </p>
            </>
          ) : (
            <div className={classes.empty}>
              <IconScale size={36} className={classes.emptyIcon} aria-hidden />
              {t('fuel.overview.reconciliationEmpty')}
            </div>
          )}
          {reportError ? (
            <Alert color="red" role="alert">
              {reportError}
            </Alert>
          ) : null}
          <div className={classes.panelFoot}>
            <Button
              variant="outline"
              leftSection={<IconFileText size={18} />}
              disabled={!recon || recon.fuels.length === 0}
              loading={reporting}
              onClick={() => void createReport()}
            >
              {t('fuel.overview.createReport')}
            </Button>
          </div>
        </section>
      </div>
    </div>
  );
}
